import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { useMemo, useEffect } from 'react';
import { isObject, omit } from '@chakra-ui/utils';
import { warn } from './logger.js';
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
/**
 * A hook for automatically translating old to new props.
 *
 * Can output a warning in development environments.
 *
 * @example
 *   const BACKWARDS_COMPATIBILITY_MAP = {
 *     sizeVariant: {
 *       xs: {
 *         size: 'xs',
 *       },
 *       sm: {
 *         size: 'sm',
 *       },
 *       md: {
 *         size: 'md',
 *       },
 *       lg: {
 *         size: 'lg',
 *       },
 *     },
 *   };
 *
 *   const propsWithBackwardsCompatibility = useBackwardsCompatibility(
 *     BACKWARDS_COMPATIBILITY_MAP,
 *     props,
 *     { sizeVariant: `The prop 'sizeVariant' is deprecated. Please use 'size' instead.` }
 *   );
 *
 * @param compatibilityMap
 * @param props
 * @param warnings
 */

var useBackwardsCompatibility = function useBackwardsCompatibility(compatibilityMap, props, warnings) {
  // Filters out the props that need to be made backwards-compatible, e.g.
  // a given prop matches with the prop & values in the compatibilityMap.
  var propsToMakeBackwardsCompatible = useMemo(function () {
    return Object.keys(props).filter(function (key) {
      return isObject(compatibilityMap[key]) && Object.keys(compatibilityMap[key]).some(function (value) {
        return value === props[key];
      });
    });
  }, [compatibilityMap, props]); // Generate the props object with the compatibility map and it's values
  // included.

  var propsWithBackwardsCompatibility = useMemo(function () {
    // If no backwards compatibility is necessary, return the props.
    if (!propsToMakeBackwardsCompatible.length) {
      return props;
    } // Omit the props that have to be made backwards-compatible

    var returnedProps = omit(props, propsToMakeBackwardsCompatible); // 2. Insert the backwards-compatible props

    propsToMakeBackwardsCompatible.forEach(function (key) {
      returnedProps = _objectSpread(_objectSpread({}, returnedProps), compatibilityMap[key][props[key]]);
    });
    return returnedProps;
  }, [compatibilityMap, props, propsToMakeBackwardsCompatible]); // Log a warning, optionally one for each prop made backwards-compatible.

  useEffect(function () {
    if (propsToMakeBackwardsCompatible.length) {
      propsToMakeBackwardsCompatible.forEach(function (key) {
        return warn(warnings[key], "useBackwardsCompatibility");
      });
    }
  }, [propsToMakeBackwardsCompatible, warnings]);
  return propsWithBackwardsCompatibility;
};
var useBackwardsCompatibility$1 = useBackwardsCompatibility;
export { useBackwardsCompatibility$1 as default };